export class ApiError extends Error {
  constructor(message, status, detail, errorType = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.errorType = errorType;
  }
}

function parseErrorPayload(data, status) {
  if (!data || typeof data !== 'object') {
    return {
      message: `Request failed (${status})`,
      detail: data,
      errorType: null,
    };
  }

  const detail = data.detail;
  const errorType = data.error_type || data.errorType || null;

  let message;
  if (typeof detail === 'string') {
    message = detail;
  } else if (Array.isArray(detail)) {
    message = detail.map((item) => item.msg || JSON.stringify(item)).join(', ');
  } else if (detail && typeof detail === 'object') {
    message = JSON.stringify(detail);
  } else {
    message = `Request failed (${status})`;
  }

  if (errorType && !message.includes(errorType)) {
    message = `${message} [${errorType}]`;
  }

  if (status === 401) {
    message = `Not authenticated: ${message}. Try signing out and back in.`;
  } else if (status === 403) {
    message = `Access denied: ${message}`;
  } else if (status === 400 && message.includes('X-Shop-Id')) {
    message = `${message} Select a shop and try again.`;
  }

  return { message, detail, errorType };
}

async function readResponseBody(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

export async function apiRequest(baseUrl, path, options = {}) {
  const { token, shopId, method = 'GET', body, requireShopId = false } = options;

  if (!token) {
    throw new ApiError('Not signed in. Please refresh the page and log in again.', 401);
  }
  if (requireShopId && shopId == null) {
    throw new ApiError('No shop selected. Choose a shop from the selector and try again.', 400);
  }

  const headers = { Accept: 'application/json' };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  headers.Authorization = `Bearer ${token}`;
  if (shopId != null) {
    headers['X-Shop-Id'] = String(shopId);
  }

  const response = await fetch(`${baseUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 204) {
    return null;
  }

  const data = await readResponseBody(response);

  if (!response.ok) {
    const { message, detail, errorType } = parseErrorPayload(data, response.status);
    console.error('API error', { method, path, status: response.status, detail, errorType });
    throw new ApiError(message, response.status, detail, errorType);
  }

  return data;
}

export async function apiUpload(baseUrl, path, options = {}) {
  const { token, shopId, file, fields = {}, requireShopId = true } = options;

  if (!token) {
    throw new ApiError('Not signed in. Please refresh the page and log in again.', 401);
  }
  if (requireShopId && shopId == null) {
    throw new ApiError('No shop selected. Choose a shop from the selector and try again.', 400);
  }

  const formData = new FormData();
  formData.append('file', file);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });

  const headers = { Accept: 'application/json' };
  headers.Authorization = `Bearer ${token}`;
  headers['X-Shop-Id'] = String(shopId);

  const response = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (response.status === 204) {
    return null;
  }

  const data = await readResponseBody(response);

  if (!response.ok) {
    const { message, detail, errorType } = parseErrorPayload(data, response.status);
    console.error('API error', { method: 'POST', path, status: response.status, detail, errorType });
    throw new ApiError(message, response.status, detail, errorType);
  }

  return data;
}
